import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { showNotification } from '@/utils/notifications'
import { GET_AD_ASSETS } from '@/graphql/ads/ad-assets-queries'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { MIRROR_AS_AD_CREATIVE_ASSET } from '@/graphql/ads/generated-image-queries'

const useImageMirroring = ({ selectedImages }) => {
    const [isMirroring, setIsMirroring] = useState(false)
    const [adCreativeAssets, setAdCreativeAssets] = useState([])
    const [adCreativeAssetLoading, setAdCreativeAssetLoading] = useState(true)
    const [replacingFormat, setReplacingFormat] = useState([])
    const {
        id: adCreativeId,
        editCreativeId,
        setStepContentData,
        getStepContentData,
        removeStepContentData,
        campaignFormat,
        markStepAs,
        goToStep,
        campaignId,
    } = useWizard()

    useQuery(GET_AD_ASSETS, {
        variables: { campaignId, adCreativeId, hasMetaId: true },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setAdCreativeAssets(data.AdAsset?.adAssets)
            setAdCreativeAssetLoading(false)
        },
    })

    useEffect(() => {
        const replacingFormat = getStepContentData('uploadAssets')?.replaceFormat
        if (replacingFormat) setReplacingFormat(replacingFormat)
    }, [])

    const calculateNextStep = () => {
        if (campaignFormat === 'carousel') return 'carouselCards'

        return 'setMediaCrop'
    }

    const [mirrorMedia] = useMutation(MIRROR_AS_AD_CREATIVE_ASSET, {
        onCompleted: (data) => {
            setIsMirroring(false)
            const newAssetId = data.mirrorAsAdCreativeAsset[0].id

            // Clear any previously cached assetId to prevent stale data
            const targetStep = calculateNextStep()
            removeStepContentData(targetStep, 'assetId')

            goToNextStep(newAssetId)
        },
        onError: (error) => {
            setIsMirroring(false)
            const message = error.graphQLErrors?.[0]?.message || error.message || 'Failed to process images. Please try again.'
            showNotification(message, 'error', 5000, null, null, false, true)
        },
    })

    const goToNextStep = (assetId) => {
        const step = calculateNextStep()
        setStepContentData({ assetId }, step)
        markStepAs(1)
        goToStep(step)
    }

    const validate = (selectedButNotUploadedMedia) => {
        if (campaignFormat === 'carousel' && selectedImages.length === 0) return 'Please select at least one media file'
        if (campaignFormat === 'singleImage' && selectedImages.length === 0) return 'Please select an image to continue'

        // Don't return error messages for upload states - just prevent progression
        if (isMirroring) return null
        if (selectedButNotUploadedMedia.length > 0) return null

        return null
    }

    const startMirroring = async () => {
        if (selectedImages.length === 0) {
            return
        }

        const replaceExisting = !!editCreativeId || (campaignFormat !== 'carousel' && replacingFormat.length === 0)

        setIsMirroring(true)

        try {
            // Determine the source type
            let source = 'ai' // Default to 'ai'
            let ids = []

            // Check if any of the selected images have a specific source
            const hasUploadSource = selectedImages.some((item) => {
                if (typeof item === 'object' && item.source === 'upload') {
                    return true
                }
                // Check in adCreativeAssets if this ID belongs to an upload source
                const assetId = typeof item === 'string' ? item : item.id
                const asset = adCreativeAssets.find((a) => a.id === assetId || a.source_id === assetId)
                return asset && asset.source === 'upload'
            })

            const hasMetaSource = selectedImages.some((item) => {
                if (typeof item === 'object' && item.source === 'meta') {
                    return true
                }
                // Check in adCreativeAssets if this ID belongs to a meta source
                const assetId = typeof item === 'string' ? item : item.id
                const asset = adCreativeAssets.find((a) => a.id === assetId || a.source_id === assetId)
                return asset && asset.source === 'meta'
            })

            if (hasMetaSource) {
                source = 'meta'
                // For meta sources, extract URLs instead of IDs
                ids = selectedImages.map((item) => {
                    if (typeof item === 'object' && item.url) {
                        return item.url
                    }
                    // Fallback to ID if URL not available
                    return typeof item === 'string' ? item : item.id
                })
            } else if (hasUploadSource) {
                source = 'upload'
                // For upload sources, extract IDs
                ids = selectedImages.map((item) => (typeof item === 'string' ? item : item.id))
            } else {
                // For AI sources, extract IDs
                ids = selectedImages.map((item) => (typeof item === 'string' ? item : item.id))
            }

            // Filter out invalid IDs to prevent backend hang
            const validIds = ids.filter((id) => id != null && id !== '')
            if (validIds.length === 0) {
                setIsMirroring(false)
                showNotification('No valid image IDs to process. Please try selecting again.', 'error', 5000, null, null, false, true)
                return
            }

            if (!campaignId) {
                setIsMirroring(false)
                showNotification('Campaign context is missing. Please refresh and try again.', 'error', 5000, null, null, false, true)
                return
            }

            if (replacingFormat.length) removeStepContentData('uploadAssets', 'replaceFormat')

            await mirrorMedia({
                variables: {
                    ids: validIds,
                    campaignId,
                    adId: adCreativeId,
                    replaceExisting,
                    allowMultiple: campaignFormat === 'carousel',
                    formats: replacingFormat,
                    source,
                },
            })
        } catch {
            // onError handler will reset isMirroring and show notification
            setIsMirroring(false)
        }
    }

    const mirrorImageAndNavigateToNextScreen = () => {
        const firstAssetId = typeof selectedImages[0] === 'string' ? selectedImages[0] : selectedImages[0].id
        const selectedButNotUploaded = selectedImages.filter((item) => {
            const id = typeof item === 'string' ? item : item.id
            return !adCreativeAssets.find((a) => a.source_id === id && a.source === 'ai')
        })

        const errorMessage = validate(selectedButNotUploaded)

        // If currently uploading, prevent progression without showing error
        if (isMirroring || selectedImages.length > 0) {
            if (selectedImages.length > 0) startMirroring()

            window.scrollTo({ top: 0, behavior: 'smooth' })

            return
        }

        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
            showNotification(errorMessage, 'error', 3000, null, null, false, true)
            return
        }

        goToNextStep(firstAssetId)
    }

    return {
        isMirroring,
        adCreativeAssets,
        adCreativeAssetLoading,
        mirrorImageAndNavigateToNextScreen,
    }
}

export default useImageMirroring
